import { type PaginationState } from '@tanstack/react-table'
import { type Dispatch, type FC, type SetStateAction } from 'react'

import HasReceiptsFilter from 'components/admin/HasReceiptsFilter'
import OffThatchOrMedicareFilter from 'components/admin/OffThatchOrMedicareFilter'
import AfterDateFilter from 'components/brokers/needs_attention/AfterDateFilter'
import AssigneesFilter from 'components/brokers/needs_attention/AssigneesFilter'
import BeforeDateFilter from 'components/brokers/needs_attention/BeforeDateFilter'
import EmployerFilter from 'components/brokers/needs_attention/EmployerFilter'
import EnrollmentCategoryFilter from 'components/brokers/needs_attention/EnrollmentCategoryFilter'
import MemberFilter from 'components/brokers/needs_attention/MemberFilter'
import VipEmployerFilter from 'components/brokers/needs_attention/VipEmployerFilter'
import {
  type MultipleSelectedValuesFilter,
  type SingleSelectedValueFilter,
} from 'components/brokers/types'
import { type ReimbursementsFilters } from 'components/hooks/reimbursements/useGetReimbursementRequests'
import EnterpriseFiltersView, { type FilterConfigurations } from 'components/reusable/filters/EnterpriseFiltersView'
import { type ComboboxFilterGroup } from 'components/reusable/table/ComboboxMultiSelectFilter'

import ReimbursementPurposeFilter from './ReimbursementPurposeFilter'
import ReimbursementStateFilter from './ReimbursementStateFilter'

export type ReimbursementFilterGroupType =
  | 'Date'
  | 'Associations'
  | 'Reimbursement'
  | 'Triaging'

type SingleSelectFilterComponents = Record<
  string,
  FC<SingleSelectedValueFilter>
>
type MultiSelectFilterComponents = Record<
  string,
  FC<MultipleSelectedValuesFilter>
>

export const reimbursementSingleSelectFilters: SingleSelectFilterComponents = {
  after_date: AfterDateFilter,
  before_date: BeforeDateFilter,
  has_receipts: HasReceiptsFilter,
  vip_employer: VipEmployerFilter,
  off_thatch_or_medicare: OffThatchOrMedicareFilter,
}

export const reimbursementMultiSelectFilters: MultiSelectFilterComponents = {
  states: ReimbursementStateFilter,
  purposes: ReimbursementPurposeFilter,
  employer_ids: EmployerFilter,
  member_ids: MemberFilter,
  broker_ids: AssigneesFilter,
  enrollment_categories: EnrollmentCategoryFilter,
}

export const filterConfigurations: FilterConfigurations<
  ReimbursementsFilters,
  ReimbursementFilterGroupType
> = {
  after_date: {
    key: 'after_date',
    label: 'After Date',
    group: 'Date',
    isMultiValue: false,
  },
  before_date: {
    key: 'before_date',
    label: 'Before Date',
    group: 'Date',
    isMultiValue: false,
  },
  states: {
    key: 'states',
    label: 'State',
    group: 'Reimbursement',
    isMultiValue: true,
  },
  purposes: {
    key: 'purposes',
    label: 'Purpose',
    group: 'Reimbursement',
    isMultiValue: true,
  },
  employer_ids: {
    key: 'employer_ids',
    label: 'Employer',
    group: 'Associations',
    isMultiValue: true,
  },
  member_ids: {
    key: 'member_ids',
    label: 'Member',
    group: 'Associations',
    isMultiValue: true,
  },
  broker_ids: {
    key: 'broker_ids',
    label: 'Assignee',
    group: 'Triaging',
    isMultiValue: true,
  },
  has_receipts: {
    key: 'has_receipts',
    label: 'Has Receipts?',
    group: 'Reimbursement',
    isMultiValue: false,
  },
  vip_employer: {
    key: 'vip_employer',
    label: 'VIP Employer',
    group: 'Associations',
    isMultiValue: false,
  },
  off_thatch_or_medicare: {
    key: 'off_thatch_or_medicare',
    label: 'Off-Thatch/Medicare',
    group: 'Reimbursement',
    isMultiValue: false,
  },
  enrollment_categories: {
    key: 'enrollment_categories',
    label: 'Enrollment Category',
    group: 'Reimbursement',
    isMultiValue: true,
  },
}

export const reimbursementDefaultPinnedOptionsByFilterValue = {
  states: [],
  purposes: [],
  employer_ids: [],
  member_ids: [],
  broker_ids: [{ label: 'Unassigned', value: 'unassigned' }],
  enrollment_categories: [],
}

export const getReimbursementFiltersConfigurationSortValue = (
  filterConfiguration: ComboboxFilterGroup<string>
): number => {
  switch (filterConfiguration.groupLabel) {
    case 'Triaging':
      return 0
    case 'Reimbursement':
      return 1
    case 'Date':
      return 2
    case 'Associations':
      return 3
    default:
      return 4
  }
}

type ReimbursementRequestFiltersProps = {
  filters: ReimbursementsFilters
  setFilters: Dispatch<SetStateAction<ReimbursementsFilters>>
  pagination: PaginationState
  setPagination: Dispatch<SetStateAction<PaginationState>>
}

const ReimbursementRequestFilters = ({
  filters,
  setFilters,
  pagination,
  setPagination,
}: ReimbursementRequestFiltersProps) => {
  return (
    <EnterpriseFiltersView
      filters={filters}
      setFilters={setFilters}
      pagination={pagination}
      setPagination={setPagination}
      filterConfigurations={filterConfigurations}
      singleSelectFilters={reimbursementSingleSelectFilters}
      multiSelectFilters={reimbursementMultiSelectFilters}
      defaultPinnedOptionsByFilterValue={
        reimbursementDefaultPinnedOptionsByFilterValue
      }
      getFiltersConfigurationSortValue={
        getReimbursementFiltersConfigurationSortValue
      }
    />
  )
}

export default ReimbursementRequestFilters
